import './PlayersList.scss';

import { Gift, Trophy } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/store/hooks';
import type { RollsPlayer } from '@/entities/rolls';
import {
  RollsStatus,
  selectPlayers,
  selectRollsStatus,
  selectRoundIndex,
  selectTotalPot,
  selectWinnerId,
} from '@/entities/rolls';
import { convertToUsd, formatCurrencyBreakdown, formatUsd, isCurrencyCode } from '@/shared/lib/currency';
import { PlayerAvatar } from '@/shared/ui/PlayerAvatar';

/** Sum the USD value of every contribution, preferring server-provided rates. */
function totalUsd(player: RollsPlayer): number {
  return player.contributions.reduce((sum, c) => {
    if (c.amountUsd != null) return sum + c.amountUsd;
    return sum + (isCurrencyCode(c.currency) ? convertToUsd(c.amount, c.currency) : 0);
  }, 0);
}

interface PlayerRowProps {
  player: RollsPlayer;
  index: number;
  share: number;
  isWinner: boolean;
}

function PlayerRow({ player, index, share, isWinner }: PlayerRowProps) {
  const { t } = useTranslation();
  const style = {
    '--player-color': player.color,
    '--player-share': `${share}%`,
    '--item-in-delay': `${index * 0.05}s`,
  } as CSSProperties;

  const usd = totalUsd(player);
  const gift = player.contributions.find((c) => c.currency === 'GIFT');

  return (
    <div
      style={style}
      className={`players-list__item ${isWinner ? 'players-list__item--winner' : ''}`}>
      <div className="players-list__bar" aria-hidden />

      <div className="players-list__left">
        <PlayerAvatar id={player.id} avatar={player.avatar} color={player.color} size={28} />
        <div className="players-list__info">
          <span className="players-list__name">@{player.username}</span>
          <span className="players-list__bet">
            {formatCurrencyBreakdown(player.totalsByCurrency)}
            {usd > 0 && <span className="players-list__usd"> · ≈ {formatUsd(usd)}</span>}
          </span>
          {gift && (
            <span className="players-list__gift" title={gift.giftName ?? t('rolls.giftStake')}>
              {gift.giftImage ? (
                <img src={gift.giftImage} alt="" className="players-list__gift-img" />
              ) : (
                <Gift className="players-list__gift-icon" />
              )}
              <span className="players-list__gift-name">{gift.giftName ?? t('rolls.giftStake')}</span>
            </span>
          )}
        </div>
      </div>

      <div className="players-list__right">
        {isWinner && <Trophy className="players-list__trophy" />}
        <span
          className={`players-list__percentage ${isWinner ? 'players-list__percentage--winner' : ''}`}>
          {share.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

export function PlayersList() {
  const players = useAppSelector(selectPlayers);
  const totalPot = useAppSelector(selectTotalPot);
  const winnerId = useAppSelector(selectWinnerId);
  const gameStatus = useAppSelector(selectRollsStatus);
  const roundIndex = useAppSelector(selectRoundIndex);

  if (players.length === 0) return null;

  return (
    <div className="players-list">
      <h3 className="players-list__title">
        Round #{roundIndex} Players:
        <span className="players-list__count">{players.length}</span>
      </h3>

      <div className="players-list__items">
        {players.map((player, index) => (
          <PlayerRow
            key={player.id}
            player={player}
            index={index}
            share={totalPot > 0 ? (player.betAmount / totalPot) * 100 : 0}
            isWinner={gameStatus === RollsStatus.FINISHED && player.id === winnerId}
          />
        ))}
      </div>
    </div>
  );
}
